import { IconButton, RoleBadge } from '@cere/cere-design-system';
import EditIcon from '@mui/icons-material/Edit';
import type { Member } from '@cef-ai/vault-sdk';
import { ceilingForMember, orgScopeGrants, type MemberCeiling } from '@/lib/clients/vault/orgs';
import { MemberCeilingPicker } from './MemberCeilingPicker';

/** The per-row privacy-ceiling control's context, supplied only where the
 *  caller may administer the vault — the same gate as the edit affordance. */
export interface CeilingControl {
  /** The vault the ceiling writes address. */
  vaultId: string;
  /** Current SIGNED ceilings by member pubkey; a miss reads as Unknown. */
  ceilings: Record<string, MemberCeiling>;
  /** Re-read the ceilings after a committed write. */
  onSaved: () => void;
}

/**
 * The member roster of the selected org. The disclosure rule is load-bearing
 * here: `Member.scopes` travels in THREE states (a grant set, `{}`, absent =
 * not disclosed to this caller) and each must render distinctly — collapsing
 * "absent" into "no grants" would tell the user a member has no permissions
 * when the roster merely cannot see them.
 *
 * The same rule gates the row's controls: the grants editor seeds from the
 * row's `scopes`, so an undisclosed row offers NO editing (a save there
 * would revoke grants the caller never saw), and the privacy-ceiling picker
 * follows it because its write is the same owner-signed membership PUT. The
 * synthesized owner row offers neither — the owner is not a membership row,
 * so the server has no standing to sign for them and the write cannot land.
 */
export function OrgRoster({
  members,
  onEditGrants,
  ceilingControl,
}: {
  members: Member[];
  /** Present only when the caller may administer this org; absent renders the
   *  read-only roster (no per-row controls at all). */
  onEditGrants?: (member: Member) => void;
  ceilingControl?: CeilingControl;
}) {
  return (
    <ul className="vault-orgs__roster">
      {members.map((m) => (
        <li key={m.memberPubkey} className="vault-orgs__roster-row">
          <span className="vault-orgs__roster-key" title={m.memberPubkey}>{shortKey(m.memberPubkey)}</span>
          {m.isOwner
            ? <RoleBadge label="Owner" />
            : <RoleBadge label={m.role === 'admin' ? 'Admin' : 'Member'} />}
          {m.state === 'suspended' && <span className="vault-orgs__roster-state">Suspended</span>}
          <span className="vault-orgs__roster-grants">
            <MemberGrants member={m} />
          </span>
          {ceilingControl && !m.isOwner && m.scopes !== undefined && (
            <MemberCeilingPicker
              vaultId={ceilingControl.vaultId}
              member={m}
              // Canonicalised lookup (a member the signed roster does not
              // serve — suspended, expired — is Unknown, not "none signed").
              ceiling={ceilingForMember(ceilingControl.ceilings, m.memberPubkey)}
              onSaved={ceilingControl.onSaved}
            />
          )}
          {onEditGrants && !m.isOwner && m.scopes !== undefined && (
            <IconButton
              aria-label={`Edit grants for ${shortKey(m.memberPubkey)}`}
              className="vault-orgs__roster-edit"
              onClick={() => onEditGrants(m)}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </li>
      ))}
    </ul>
  );
}

function MemberGrants({ member }: { member: Member }) {
  if (member.isOwner) {
    // The owner row carries NO scopes by design: their standing over the
    // vault is key math, not a grant — saying "not visible" here would be
    // wrong (nobody is withholding it) and a grant list would invent rows.
    return <span className="vault-orgs__grant vault-orgs__grant--note">Full access — owner</span>;
  }
  if (member.scopes === undefined) {
    return <span className="vault-orgs__grant vault-orgs__grant--muted">Not visible to your role</span>;
  }
  const grants = orgScopeGrants(member.scopes);
  if (grants.length === 0) {
    return <span className="vault-orgs__grant vault-orgs__grant--note">No scope grants</span>;
  }
  return (
    <>
      {grants.map((g) => (
        <span key={g.name} className="vault-orgs__grant">{g.name} · {g.level}</span>
      ))}
    </>
  );
}

/** Pubkeys are 0x + 64 hex — unreadable in full. Short form keeps rows
 *  scannable; the full key stays available on the row's title. */
function shortKey(key: string): string {
  if (key.length <= 16) return key;
  return `${key.slice(0, 10)}…${key.slice(-8)}`;
}
